import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Twitter, Youtube, Instagram } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const Footer = () => {
  return (
    <footer className="bg-black text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Column 1: About SimplA */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-lg sm:text-xl font-bold text-white">Globaltis IA</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              La IA que funciona. Sin complicaciones. Transformamos negocios con soluciones de inteligencia artificial innovadoras y personalizadas.
            </p>
            <p className="text-white/60 text-xs italic">
              "IA que impulsa tu negocio"
            </p>
          </div>

          {/* Column 2: Quick Navigation */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-white">Navegación</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-white/70 hover:text-white hover:underline transition-colors text-sm active:opacity-70">
                  Inicio
                </Link>
              </li>
              <li>
                <Link to="/nosotros" className="text-white/70 hover:text-white hover:underline transition-colors text-sm active:opacity-70">
                  Nosotros
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/70 hover:text-white hover:underline transition-colors text-sm active:opacity-70">
                  News
                </Link>
              </li>
              <li>
                <Link to="/capsulas" className="text-white/70 hover:text-white hover:underline transition-colors text-sm active:opacity-70">
                  Cápsulas
                </Link>
              </li>
              <li>
                <Link to="/cursos" className="text-white/70 hover:text-white hover:underline transition-colors text-sm active:opacity-70">
                  Itinerarios
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Services */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-white">
              <Link to="/servicios" className="hover:underline">Servicios</Link>
            </h4>
            <ul className="space-y-2">
              <li><Link to="/servicios/automatizacion" className="text-white/70 hover:text-white hover:underline transition-colors text-sm">Automatización de Procesos</Link></li>
              <li><Link to="/servicios/atencion-cliente" className="text-white/70 hover:text-white hover:underline transition-colors text-sm">Atención al Cliente 24/7</Link></li>
              <li><Link to="/servicios/contenido-ia" className="text-white/70 hover:text-white hover:underline transition-colors text-sm">Contenido Profesional con IA</Link></li>
              <li><Link to="/servicios/presencia-digital" className="text-white/70 hover:text-white hover:underline transition-colors text-sm">Presencia Digital Express</Link></li>
              <li><Link to="/servicios/gestion-administrativa" className="text-white/70 hover:text-white hover:underline transition-colors text-sm">Gestión Administrativa</Link></li>
              <li><Link to="/servicios/diagnostico-ia" className="text-white/70 hover:text-white hover:underline transition-colors text-sm">Diagnóstico de Madurez IA</Link></li>
            </ul>
          </div>

          {/* Column 4: Contacto */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-white">Contacto</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-white/70 text-sm">
                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <a href="tel:+34916651232" className="hover:text-white hover:underline transition-colors active:opacity-70">
                  +34 91 665 12 32
                </a>
              </li>
              <li className="flex items-start gap-2 text-white/70 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Madrid, España</span>
              </li>
              <li className="text-white/60 text-xs mt-2">
                Lun - Vie: 9:00 - 18:00
              </li>
            </ul>
            
            {/* CTA al formulario */}
            <div className="pt-4">
              <Link 
                to="/contacto" 
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-black rounded-full hover:bg-gray-200 active:bg-gray-300 transition-all text-sm font-medium touch-manipulation"
              >
                <Mail className="w-4 h-4" />
                Enviar mensaje
              </Link>
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10">
          <div className="flex justify-center gap-6 sm:gap-8">
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white active:opacity-70 transition-colors touch-manipulation"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white active:opacity-70 transition-colors touch-manipulation"
              aria-label="Twitter"
            >
              <Twitter className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
            <a
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white active:opacity-70 transition-colors touch-manipulation"
              aria-label="YouTube"
            >
              <Youtube className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white active:opacity-70 transition-colors touch-manipulation"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>
          </div>
        </div>
      </div>


      {/* Bottom Bar - Copyright and Legal */}
      <div className="border-t border-white/10 py-4 sm:py-6">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/60">
            <p className="text-center md:text-left">© 2025 Globaltis IA. Todos los derechos reservados.</p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              <a href="#" className="hover:text-white hover:underline active:opacity-70 transition-colors">
                Política de Privacidad
              </a>
              <span className="hidden md:inline">|</span>
              <a href="#" className="hover:text-white hover:underline active:opacity-70 transition-colors">
                Términos y Condiciones
              </a>
              <span className="hidden md:inline">|</span>
              <a href="#" className="hover:text-white hover:underline active:opacity-70 transition-colors">
                Aviso Legal
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
